/**
 * Sprint 006 A4 (Issue #6, kleine Ausbaustufe) — persistierte
 * Quartals-Gruppierung für /invoices.
 *
 * Tri-State-Auflösung: URL (?groupBy=) > localStorage > Default
 * ('quarter'). Präzedenz-Muster wie usePageSizePref (T573): eine gültige
 * URL-Angabe wird in localStorage gespiegelt, damit der nächste Besuch
 * ohne Param die letzte echte Nutzer-Wahl erinnert.
 */
import { useCallback, useEffect, useState } from 'react';
import type { InvoiceGroupBy } from '../components/invoices/InvoiceListView';

const STORAGE_KEY = 'invoice_list_group_by';
const DEFAULT_GROUP_BY: InvoiceGroupBy = 'quarter';

function isValidGroupBy(v: unknown): v is InvoiceGroupBy {
  return v === 'none' || v === 'quarter';
}

function readPref(): InvoiceGroupBy {
  if (typeof window === 'undefined') return DEFAULT_GROUP_BY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isValidGroupBy(raw) ? raw : DEFAULT_GROUP_BY;
  } catch {
    return DEFAULT_GROUP_BY;
  }
}

function writePref(value: InvoiceGroupBy): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // private window / quota — degrade silently
  }
}

export function useInvoiceGroupByPref(urlGroupBy?: InvoiceGroupBy): {
  groupBy: InvoiceGroupBy;
  setGroupBy: (next: InvoiceGroupBy) => void;
} {
  const [stored, setStored] = useState<InvoiceGroupBy>(() => readPref());

  const groupBy: InvoiceGroupBy = isValidGroupBy(urlGroupBy) ? urlGroupBy : stored;

  useEffect(() => {
    if (isValidGroupBy(urlGroupBy) && urlGroupBy !== stored) {
      writePref(urlGroupBy);
      setStored(urlGroupBy);
    }
  }, [urlGroupBy, stored]);

  const setGroupBy = useCallback((next: InvoiceGroupBy) => {
    writePref(next);
    setStored(next);
  }, []);

  return { groupBy, setGroupBy };
}
